<template>
  <modal :visible.sync="innerVisible" :title="title" width="1000px" top="20px" append-to-body>
    <div class="gallery-container">
      <!-- 有图片时的预览状态 -->
      <div v-if="pictureList.length > 0" class="content-wrapper">
        <!-- 上一张按钮 -->
        <div
          class="arrow-btn left-arrow"
          :class="{ disabled: currentIndex === 0 }"
          @click="prevImage"
        >
          <i class="el-icon-arrow-left"></i>
        </div>

        <!-- 中间内容区 -->
        <div class="preview-main">
          <!-- 图片展示区 -->
          <div class="preview-image-box">
            <!-- 上传区域（当当前图片是上传类型时显示） -->
            <el-upload
              v-if="pictureList[currentIndex].type"
              action="#"
              :http-request="handleUpload"
              :show-file-list="false"
              accept="image/*"
              class="center-upload"
            >
              <div class="upload-icon-box large">
                <i class="el-icon-plus"></i>
              </div>
            </el-upload>

            <!-- 图片（当当前图片不是上传类型时显示） -->
            <template v-else>
              <img
                :src="getPictureUrl(pictureList[currentIndex].picture)"
                class="preview-img"
              />
              <!-- 悬浮：右上方白色删除按钮 -->
              <div class="hover-delete-btn" @click="handleDelete(currentIndex)">
                <i class="el-icon-delete"></i>
              </div>

              <!-- 底部半透明标题栏（可编辑） -->
              <div class="image-caption">
                <div class="caption-text">
                  <!-- 普通显示模式 -->
                  <div v-if="!isEditing" class="caption-display">
                    {{ pictureList[currentIndex].description || 'New Position' }}
                  </div>

                  <!-- 编辑模式 -->
                  <div v-else class="caption-edit">
                    <el-input
                      v-model="pictureList[currentIndex].description"
                      size="small"
                      @blur="handleSaveDesc"
                      @keyup.enter="handleSaveDesc"
                    />
                  </div>
                </div>
                <div class="caption-actions">
                  <!-- 编辑/保存按钮 -->
                  <i
                    v-if="!isEditing"
                    class="el-icon-edit"
                    @click="startEditing"
                  ></i>
                  <i
                    v-else
                    class="el-icon-check"
                    @click="handleSaveDesc"
                  ></i>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 下一张按钮 -->
        <div
          class="arrow-btn right-arrow"
          @click="nextImage"
        >
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>

      <!-- 无图片时的空状态 -->
      <div v-else class="empty-preview-box">
        <el-upload
          action="#"
          :http-request="handleUpload"
          :show-file-list="false"
          accept="image/*"
          class="center-upload"
        >
          <div class="upload-icon-box large">
            <i class="el-icon-plus"></i>
            <span>{{$t('integration.upload_picture')}}</span>
          </div>
        </el-upload>
      </div>
    </div>
  </modal>
</template>

<script>
import {
  getCarrierDevicePicture,
  saveCarrierDevicePicture,
  deleteCarrierDevicePicture,
  uploadCarrierDevicePictureFile,
} from '@/api/modules/carrier.js'

export default {
  name: 'DevicePicture',
  props: {
    visibleShow: {
      type: Boolean,
      default: false
    },
    carrierDeviceId: {
      type: Number,
    }
  },
  data () {
    return {
      title: this.$t('integration.installation_point') +' '+ this.$t('integration.picture'),
      pictureList: [],
      currentIndex: 0,
      isEditing: false, // 是否处于编辑模式
    }
  },
  computed: {
    // 判断是否是最后一张图片
    isLastImage() {
      return this.currentIndex === this.pictureList.length - 1
    },
    // 中转 prop，避免直接修改 prop
    innerVisible: {
      get () {
        return this.visibleShow
      },
      set (val) {
        this.$emit('update:visibleShow', val)
      }
    }
  },
  watch: {
    carrierDeviceId(newVal) {
      if (newVal) {
        this.loadPictures()
      }
    },
    visibleShow(newVal) {
      if (newVal && this.carrierDeviceId) {
        this.loadPictures()
      }
    }
  },
  methods: {
    /**
     * 获取图片 URL
     * 兼容 MinIO 模式（完整 URL）和本地磁盘模式（/upload/... 相对路径，需拼接 /service 前缀）
     */
    getPictureUrl(picture) {
      if (!picture) return ''
      // MinIO 模式返回的是完整 URL（http:// 或 https://）
      if (picture.startsWith('http://') || picture.startsWith('https://')) {
        return picture
      }
      // 兼容模式：本地磁盘相对路径，拼接 /service 前缀
      return '/service' + picture
    },

    loadPictures() {
      getCarrierDevicePicture({"carrierDeviceId": this.carrierDeviceId}).then(({ result }) => {
        this.pictureList = result || []
        // 添加一个上传类型的项（如果需要）
        this.pictureList.push({type: 1})
        this.currentIndex = 0
      }).catch(error => {
        console.error('加载图片失败:', error)
      })
    },

    prevImage() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
      if (this.isEditing){
        this.isEditing = false
      }
    },

    nextImage() {
      if (this.currentIndex < this.pictureList.length - 1) {
        this.currentIndex++
      }
      if (this.isEditing){
        this.isEditing = false
      }
    },

    // 开始编辑
    startEditing() {
      this.isEditing = true
    },

    // 保存描述
    handleSaveDesc() {
      const currentPic = this.pictureList[this.currentIndex]
      if (!currentPic.id) return

      saveCarrierDevicePicture({
        id: currentPic.id,
        description: currentPic.description
      }).then(() => {
        this.$message.success($t('public.tip_save_succ'))
        this.isEditing = false
      }).catch(() => {
        this.$message.success($t('public.tip_oper_fail'))
      })
    },

    // 删除图片
    handleDelete(index) {
      this.$confirm($t('integration.are_you_sure_you_want_to_delete_picture'),   $t('public.prompt'), {
        confirmButtonText: $t('public.btn_confirm'),
        cancelButtonText:  $t('public.btn_cancel'),
        type: 'warning'
      }).then(() => {
        const currentPic = this.pictureList[index]
        deleteCarrierDevicePicture( currentPic.id).then(() => {
          this.$message.success($t('public.tip_del_succ'))
          this.loadPictures()
        }).catch(() => {
          this.$message.success($t('public.tip_del_fail'))
        })
      }).catch(() => {})
    },

    handleUpload(options) {
      const formData = new FormData()
      formData.append('uploadFile', options.file)
      uploadCarrierDevicePictureFile(formData).then(res => {
        saveCarrierDevicePicture({
          "carrierDeviceId": this.carrierDeviceId,
          "picture": res.result
        }).then( data =>{
          this.loadPictures()
        })
      }).catch(error => {
        console.error('上传失败:', error)
      })
    },
  },
}
</script>

<style scoped>
/* 整体容器 */
.gallery-container {
  height: 520px;
  background-color: #f5f7fa;
  border-radius: 4px;
  overflow: hidden;
}

/* 无图片时的空状态 */
.empty-preview-box {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.center-upload .upload-icon-box.large {
  width: 200px;
  height: 200px;
  font-size: 16px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #909399;
  transition: all 0.3s;
}

.center-upload .upload-icon-box.large i {
  font-size: 40px;
  margin-bottom: 10px;
}

.center-upload .upload-icon-box.large:hover {
  border-color: #409EFF;
  color: #409EFF;
}

/* 有图片时的横向布局 */
.content-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 15px;
  box-sizing: border-box;
}

/* 中间主体区域 */
.preview-main {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 15px;
  overflow: hidden;
}

/* 图片展示区 */
.preview-image-box {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background-color: #fff;
  border-radius: 4px;
  margin-bottom: 15px;
  overflow: hidden;
}

.preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 悬浮：白色删除按钮（默认显示） */
.hover-delete-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #606266;
  font-size: 16px;
}

.hover-delete-btn:hover {
  background: #F56C6C;
  color: #fff;
}

/* 底部半透明标题栏 */
.image-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.4);
  padding: 12px 15px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.caption-text {
  color: #fff;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
}

/* 显示模式 */
.caption-display {
  cursor: pointer;
}

/* 编辑模式 */
.caption-edit {
  width: 100%;
}

/* 编辑/保存按钮 */
.caption-actions i {
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.caption-actions i:hover {
  opacity: 1;
}

/* 左侧切换按钮 */
.arrow-btn {
  width: 36px;
  height: 60px;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.3s;
  font-size: 18px;
  flex-shrink: 0;
}

.arrow-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.arrow-btn.disabled {
  background: rgba(0, 0, 0, 0.1);
  cursor: not-allowed;
  color: #ccc;
}
</style>
